// Cascada departamento → municipio sobre un solo Row (apilado en xs, dos
// columnas desde sm).
// Los Form.Item viven dentro del componente con names fijos
// (idDepartamento / idMunicipio) para que las reglas required del form padre
// funcionen. Al cambiar de departamento se limpia el municipio seleccionado.
import { Col, Form, Row, Select } from 'antd';
import type { Rule } from 'antd/es/form';
import { useT } from '../locales';
import { useCatalogos } from '../features/catalogos/api/useCatalogos';
import { filterOptionByLabel } from '../lib/normalizeSearchText';

// Guatemala tiene ~340 municipios; el límite del backend (QueryCatalogosDtoSchema)
// es 1000, así que el catálogo entra completo en una página.
const LIMITE_MUNICIPIOS = 1000;

interface DepartamentoMunicipioSelectProps {
  departamentoLabel?: string;
  municipioLabel?: string;
  departamentoRules?: Rule[];
  municipioRules?: Rule[];
  disabled?: boolean;
}

export function DepartamentoMunicipioSelect({
  departamentoLabel,
  municipioLabel,
  departamentoRules,
  municipioRules,
  disabled,
}: DepartamentoMunicipioSelectProps) {
  const t = useT();
  const form = Form.useFormInstance();
  const {
    data: departamentos,
    isLoading: cargandoDepartamentos,
    error: errorDepartamentos,
  } = useCatalogos('DEPARTAMENTO');
  const {
    data: municipios,
    isLoading: cargandoMunicipios,
    error: errorMunicipios,
  } = useCatalogos('MUNICIPIO', { limit: LIMITE_MUNICIPIOS });

  const idDepartamento = Form.useWatch('idDepartamento', form);
  // Los ids llegan como string del API (bigint serializado): normalizar ambos
  // lados de la comparación a number para no romper la cascada.
  const idDepartamentoNumero = idDepartamento == null ? undefined : Number(idDepartamento);
  const departamentoSeleccionado =
    idDepartamentoNumero !== undefined && Number.isFinite(idDepartamentoNumero);
  const municipiosFiltrados = departamentoSeleccionado
    ? (municipios ?? []).filter((m) => Number(m.parent) === idDepartamentoNumero)
    : [];

  // Control explícito del store: al cambiar o limpiar departamento, el municipio
  // seleccionado se descarta (un municipio no puede vivir sin su departamento).
  const handleDepartamentoChange = (id?: number) => {
    form.setFieldsValue({ idDepartamento: id, idMunicipio: undefined });
  };

  return (
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item
          style={{ marginBottom: 4 }}
          name="idDepartamento"
          label={departamentoLabel ?? t('pages.servicios.departamento')}
          rules={departamentoRules}
        >
          <Select
            style={{ width: '100%' }}
            loading={cargandoDepartamentos}
            disabled={disabled}
            allowClear
            showSearch={{ filterOption: filterOptionByLabel }}
            defaultActiveFirstOption={false}
            notFoundContent={
              cargandoDepartamentos
                ? t('common.loading')
                : errorDepartamentos
                  ? t('common.error')
                  : t('common.noResults')
            }
            placeholder={t('pages.servicios.selectDepartamento')}
            value={idDepartamento}
            onChange={handleDepartamentoChange}
            options={(departamentos ?? []).map((d) => ({
              value: Number(d.id),
              label: d.value ?? d.key ?? `#${d.id}`,
            }))}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item
          style={{ marginBottom: 4 }}
          name="idMunicipio"
          label={municipioLabel ?? t('pages.servicios.municipio')}
          rules={municipioRules}
          extra={
            !departamentoSeleccionado ? t('pages.servicios.municipioDependencyHint') : undefined
          }
        >
          <Select
            style={{ width: '100%' }}
            loading={cargandoMunicipios}
            disabled={disabled || !departamentoSeleccionado}
            allowClear
            showSearch={{ filterOption: filterOptionByLabel }}
            defaultActiveFirstOption={false}
            notFoundContent={
              cargandoMunicipios
                ? t('common.loading')
                : errorMunicipios
                  ? t('common.error')
                  : t('common.noResults')
            }
            placeholder={t('pages.servicios.selectMunicipio')}
            options={municipiosFiltrados.map((m) => ({
              value: Number(m.id),
              label: m.value ?? m.key ?? `#${m.id}`,
            }))}
          />
        </Form.Item>
      </Col>
    </Row>
  );
}
